import React, { useState, useEffect, useCallback } from 'react';
import { collection, collectionGroup, getDocs, query, where, orderBy, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { classInfoService } from '../services/ClassInfoService';
import './ParentMessagesView.css';

function ParentMessagesView({ parentData }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [showNewChatModal, setShowNewChatModal] = useState(false);

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      
      const conversationsQuery = query(
        collection(db, 'teacherParentConversations'),
        where('parentId', '==', parentData.id),
        orderBy('lastMessageAt', 'desc')
      );
      
      const conversationsSnapshot = await getDocs(conversationsQuery);
      const conversationsData = conversationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setConversations(conversationsData);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [parentData]);

  const loadMessages = useCallback(async () => {
    try {
      const messagesQuery = query(
        collection(db, 'teacherParentConversations', selectedConversation.id, 'messages'),
        orderBy('sentAt', 'asc')
      );
      
      const messagesSnapshot = await getDocs(messagesQuery);
      const messagesData = messagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(messagesData);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }, [selectedConversation]);

  const loadAvailableTeachers = useCallback(async () => {
    try {
      const linkedChildrenIds = parentData.linkedChildrenIds || [];
      if (linkedChildrenIds.length === 0) return;

      // Find all students matching the linked children IDs
      const studentsQuery = query(collectionGroup(db, 'students'), where('studentId', 'in', linkedChildrenIds));
      const studentsSnapshot = await getDocs(studentsQuery);

      const teachers = [];
      const seenTeacherIds = new Set();

      for (const studentDoc of studentsSnapshot.docs) {
        const studentData = studentDoc.data();
        const classId = studentData.classId;
        if (!classId) continue;

        const classInfo = await classInfoService.getClassInfo(classId);
        const studentName = studentData.fullName || `${studentData.firstName || ''} ${studentData.lastName || ''}`.trim();

        // Add homeroom teacher
        if (classInfo.teacherId && !seenTeacherIds.has(classInfo.teacherId)) {
          seenTeacherIds.add(classInfo.teacherId);
          teachers.push({
            teacherId: classInfo.teacherId,
            teacherName: classInfo.teacherName,
            role: 'Homeroom Teacher',
            studentId: studentData.studentId,
            studentName,
            classId
          });
        }

        // Add subject teachers
        if (classInfo.subjectTeachers) {
          for (const st of classInfo.subjectTeachers) {
            const key = `${st.teacherId}-${studentData.studentId}`;
            if (!seenTeacherIds.has(key)) {
              seenTeacherIds.add(key);
              teachers.push({
                teacherId: st.teacherId,
                teacherName: st.teacherName,
                role: `${st.subject} Teacher`,
                studentId: studentData.studentId,
                studentName,
                classId
              });
            }
          }
        }
      }

      setAvailableTeachers(teachers);
    } catch (error) {
      console.error('Error loading available teachers:', error);
    }
  }, [parentData]);

  useEffect(() => {
    if (parentData) {
      loadConversations();
      loadAvailableTeachers();
    }
  }, [parentData, loadConversations, loadAvailableTeachers]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages();
    }
  }, [selectedConversation, loadMessages]);

  const startNewConversation = async (teacher) => {
    try {
      // Check if conversation already exists
      const existingQuery = query(
        collection(db, 'teacherParentConversations'),
        where('parentId', '==', parentData.id),
        where('teacherId', '==', teacher.teacherId),
        where('studentId', '==', teacher.studentId)
      );
      const existingSnapshot = await getDocs(existingQuery);

      if (!existingSnapshot.empty) {
        const existing = { id: existingSnapshot.docs[0].id, ...existingSnapshot.docs[0].data() };
        setSelectedConversation(existing);
        setShowNewChatModal(false);
        return;
      }

      // Create new conversation
      const parentName = parentData.firstName && parentData.lastName
        ? `${parentData.firstName} ${parentData.lastName}`.trim()
        : parentData.firstName || parentData.email || 'Parent';

      const conversationData = {
        parentId: parentData.id,
        parentName,
        teacherId: teacher.teacherId,
        teacherName: teacher.teacherName,
        studentId: teacher.studentId,
        studentName: teacher.studentName,
        teacherRole: teacher.role,
        lastMessage: '',
        lastMessageAt: serverTimestamp(),
        unreadCount: 0,
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'teacherParentConversations'), conversationData);
      const newConversation = { id: docRef.id, ...conversationData };
      setConversations([newConversation, ...conversations]);
      setSelectedConversation(newConversation);
      setShowNewChatModal(false);
    } catch (error) {
      console.error('Error starting new conversation:', error);
      alert('Failed to start conversation');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const messageData = {
        conversationId: selectedConversation.id,
        senderId: parentData.id,
        senderName: parentData.firstName && parentData.lastName 
          ? `${parentData.firstName} ${parentData.lastName}`.trim()
          : parentData.firstName || parentData.email || 'Parent',
        senderRole: 'parent',
        recipientId: selectedConversation.teacherId,
        recipientName: selectedConversation.teacherName,
        message: newMessage.trim(),
        studentId: selectedConversation.studentId,
        studentName: selectedConversation.studentName,
        isRead: false,
        sentAt: serverTimestamp()
      };

      await addDoc(
        collection(db, 'teacherParentConversations', selectedConversation.id, 'messages'),
        messageData
      );

      setNewMessage('');
      loadMessages();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="messages-container">
      <div className="messages-header">
        <h2>Messages</h2>
        <p>Communicate with your child's teachers</p>
      </div>

      <div className="messages-content">
        {conversations.length === 0 && availableTeachers.length === 0 ? (
          <div className="empty-state">
            <p>No conversations yet</p>
            <p className="empty-subtitle">Link a child to start messaging teachers</p>
          </div>
        ) : (
          <>
          {availableTeachers.length > 0 && (
            <button className="new-chat-button" onClick={() => setShowNewChatModal(true)}>
              + Start New Conversation
            </button>
          )}
          {conversations.length === 0 ? (
            <div className="empty-state">
              <p>No conversations yet</p>
              <p className="empty-subtitle">Click above to start chatting with your child's teachers</p>
            </div>
          ) : (
          <div className="conversations-grid">
            <div className="conversations-list">
              <h3>Conversations</h3>
              {conversations.map((conversation, index) => (
                <div
                  key={index}
                  className={`conversation-item ${selectedConversation?.id === conversation.id ? 'active' : ''}`}
                  onClick={() => setSelectedConversation(conversation)}
                >
                  <div className="conversation-header">
                    <h4>{conversation.teacherName}</h4>
                    <span className="student-name">{conversation.studentName}</span>
                  </div>
                  <div className="conversation-preview">
                    <p>{conversation.lastMessage}</p>
                    <span className="message-time">
                      {conversation.lastMessageAt?.toDate?.() ? 
                        new Date(conversation.lastMessageAt.toDate()).toLocaleDateString() : 
                        'Recently'}
                    </span>
                  </div>
                  {conversation.unreadCount > 0 && (
                    <div className="unread-badge">
                      {conversation.unreadCount}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="messages-panel">
              {selectedConversation ? (
                <>
                  <div className="chat-header">
                    <h3>{selectedConversation.teacherName}</h3>
                    <p>Regarding: {selectedConversation.studentName}</p>
                  </div>

                  <div className="messages-list">
                    {messages.map((message, index) => (
                      <div
                        key={index}
                        className={`message ${message.senderRole === 'parent' ? 'sent' : 'received'}`}
                      >
                        <div className="message-content">
                          <p>{message.message}</p>
                          <span className="message-time">
                            {message.sentAt?.toDate?.() ? 
                              new Date(message.sentAt.toDate()).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 
                              ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <form onSubmit={sendMessage} className="message-input">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      disabled={sending}
                    />
                    <button type="submit" disabled={sending || !newMessage.trim()}>
                      {sending ? 'Sending...' : 'Send'}
                    </button>
                  </form>
                </>
              ) : (
                <div className="no-conversation-selected">
                  <p>Select a conversation to start messaging</p>
                </div>
              )}
            </div>
          </div>
          )}
          </>
        )}
      </div>

      {showNewChatModal && (
        <div className="modal-overlay" onClick={() => setShowNewChatModal(false)}>
          <div className="new-chat-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Start New Conversation</h3>
            <p className="modal-subtitle">Select a teacher to message</p>
            <div className="teacher-list">
              {availableTeachers.map((teacher, index) => (
                <div
                  key={index}
                  className="teacher-list-item"
                  onClick={() => startNewConversation(teacher)}
                >
                  <div className="teacher-avatar">👤</div>
                  <div className="teacher-details">
                    <h4>{teacher.teacherName}</h4>
                    <span className="teacher-role">{teacher.role}</span>
                    <span className="teacher-student">{teacher.studentName}</span>
                  </div>
                </div>
              ))}
            </div>
            <button className="cancel-chat-button" onClick={() => setShowNewChatModal(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParentMessagesView;
